import { type LocationRoute, type LocationSearchResult } from '@/api/accommodation/types'

// Fractions of the canvas kept clear of platform UI, per edge.
export interface SafeArea {
  top: number
  bottom: number
  right: number
}

// Social network export formats. `safeArea` keeps important content clear of
// the platform UI drawn over the image; `story` marks a 24-hour story (the
// banner may then use relative time and has no carousel to swipe).
//
// TikTok gets the same 9:16 canvas as a story but its own safe area: a photo
// post's detail crops it to ~3:4 (~12.5 % off the top and bottom) and lays the
// search bar and slide counter over the top, the For You feed puts the caption
// over the bottom quarter, and the avatar/like column sits on the right.
export const SHARE_FORMATS = {
  'ig-story': {
    width: 1080,
    height: 1920,
    story: true,
    safeArea: { top: 0.14, bottom: 0.18, right: 0 },
  },
  tiktok: {
    width: 1080,
    height: 1920,
    story: false,
    safeArea: { top: 0.24, bottom: 0.25, right: 0.14 },
  },
  'ig-post': { width: 1080, height: 1080, story: false, safeArea: null },
  'ig-portrait': { width: 1080, height: 1350, story: false, safeArea: null },
  'fb-post': { width: 1200, height: 630, story: false, safeArea: null },
  'og-image': { width: 1200, height: 630, story: false, safeArea: null },
} as const satisfies Record<string, { width: number; height: number; story: boolean; safeArea: SafeArea | null }>

export type ShareFormat = keyof typeof SHARE_FORMATS

// Visual variants of the card. Each variant is a completely separate design but
// renders into the same target dimensions.
export const SHARE_VARIANTS = [
  'a',
  'bl',
  'br',
  'cl',
  'cr',
  'd',
  'el',
  'er',
  'f',
  'f1',
  'f2',
  'g',
  'tomorrow',
  'rating',
  'ugc',
  'tl',
  'tr',
] as const

export type ShareVariant = (typeof SHARE_VARIANTS)[number]

// Props shared by every variant. A variant is fully responsible for its own
// layout; it only receives the offer data and the target canvas size.
export interface ShareCardVariantProps {
  searchResult: LocationSearchResult
  /** Target export width in pixels (social network format). */
  width: number
  /** Target export height in pixels. */
  height: number
  /** Index into the location photos (sorted by priority) used as background. */
  photoIndex?: number
  /** Edges kept clear of platform UI (stories, TikTok); none for plain images. */
  safeArea?: SafeArea | null
  /** A 24-hour story: relative time is fine, there is no carousel to swipe. */
  story?: boolean
  /** Number of slides in the post (1 = the banner alone); variants may hint at swiping. */
  carousel?: number
  /** Driving route from Prague, when the caller prefetched it; variants may show the time. */
  pragueRoute?: LocationRoute
}
